import moment from 'moment';
import { cloneDeep, head, map } from 'lodash';

class BasketCreateController {

    constructor($state, eventsService, basket, messageService, availabilityService, quantityService, propertyService, addGuestService, suggestionsService, itemService, loadingService) {
        this.$state = $state;
        this.eventsService = eventsService;
        this.basket = basket;
        this.messageService = messageService;
        this.availabilityService = availabilityService;
        this.quantityService = quantityService;
        this.propertyService = propertyService;
        this.addGuestService = addGuestService;
        this.suggestionsService = suggestionsService;
        this.itemService = itemService;
        this.loadingService = loadingService;
    }

    $onInit () {
        this.loadingService.startLoading(['basket', 'events', 'categories']);

        this.initEventFilters();

        this.activeEvents = {
            treatments: false,
            spaDays: false,
            activities: false
        };

        this.originalData = {};

        return Promise.all([
            this.getBasket(),
            this.getAllEvents(),
            this.getCategories(),
        ]).then(() => {
            this.filterEvents();
        });
    }

    getBasket () {
        return this.basket.get()
            .then(response => {
                this.loadingService.stopLoading('basket');
            });
    }

    getAllEvents () {
        return this.eventsService.collectActive(true)
            .then(response => {
                this.originalData.events = response;
                this.collectDurations();
            })
            .catch(error => this.displayError(error))
            .then(() => {
                this.loadingService.stopLoading('events');
            });
    }

    getCategories () {
        return this.eventsService.collectCategoriesFlat()
            .then(response => {
                this.originalData.categories = response;
            })
            .catch(error => this.displayError(error))
            .then(() => {
                this.loadingService.stopLoading('categories');
            });
    }

    collectDurations () {
        this.originalData.durations = {
            Treatment: [],
            SpaDay: []
        };

        this.originalData.events.forEach(event => {
            let duration = event.duration;
            let container = this.originalData.durations[event.type_name];

            if (event.display_duration) {
                duration = event.display_duration;
            }

            if (container && container.indexOf(duration) === -1) {
                container.push(duration);
            }
        });

        this.originalData.durations.Treatment.sort((a, b) => a - b);
        this.originalData.durations.SpaDay.sort((a, b) => a - b);
    }

    filterEvents (resetSelectedEvent = true) {
        this.resetAvailability(resetSelectedEvent);

        if (!this.originalData) {
            return;
        }

        this.data = cloneDeep(this.originalData);
        this.data.events = this.data.events.filter(event => {
            if (event.type_name === 'Treatment'
                && !this.eventFilters.treatments) {
                return false;
            }

            if (event.type_name === 'SpaDay'
                && !this.eventFilters.spaDays) {
                return false;
            }

            if (event.type_name === 'Activity'
                && !this.eventFilters.activities) {
                return false;
            }

            if (this.eventFilters.category
                && (!event.categories
                    || event.categories.indexOf(parseInt(this.eventFilters.category))) === -1) {
                return false;
            }

            if (this.eventFilters.duration
                && (event.display_duration && `${event.display_duration}` !== `${this.eventFilters.duration}`
                    || !event.display_duration && `${event.duration}` !== `${this.eventFilters.duration}`)) {
                return false;
            }

            switch (event.type_name) {
                case 'Treatment':
                    this.activeEvents.treatments = true;
                    break;
                case 'SpaDay':
                    this.activeEvents.spaDays = true;
                    break;
                case 'Activity':
                    this.activeEvents.acitivies = true;
                    break;
            }

            return true;
        });

        if (!this.data.events.length) {
            this.noEvents = true;
        }
    }

    resetFilters () {
        this.initEventFilters();
        this.noEvents = false;

        this.filterEvents();
    }

    initEventFilters () {
        this.eventFilters = {
            treatments: true,
            spaDays: true,
            activities: false,
            category: '',
            duration: ''
        };
    }

    resetAvailability (resetEvent = true, resetGuests = false) {
        this.itemService.setItem(null);
        this.staff = undefined;
        this.noEvents = false;

        this.availabilityService.resetAvailability();
        this.availabilityService.resetSingleDays();
        this.availabilityService.resetSingleTimes();

        if (resetEvent) {
            this.selectedEvent = '';
        }

        if (resetGuests) {
            this.guests = [];
        }
    }

    displayError (error) {
        this.messageService.failure(error);
        this.messageService.push();
        this.messageService.autoClear();
    }

    getQuantity (numberOfGuests = undefined) {
        let item = this.itemService.getItem();

        if (item) {
            item.quantity = undefined;

            if (item.preferred_time) {
                this.loadingService.startLoading('quantity');
                this.resetStaff();

                return this.quantityService.getQuantity(item, numberOfGuests).then(response => {
                    this.addGuestService.setState(response.quantity, 'maximumGuests');

                    item.quantity = response.quantity;
                    this.itemService.setItem(item);

                    this.setStaff(response.staff);

                    this.loadingService.stopLoading('quantity');
                });
            }
        }
    }

    setStaff (staff) {
        this.staff = undefined;

        if (this.canDisplayStaff()) {
            this.staff = map(staff, (staffId, staffName) => {
                return {
                    id: staffId,
                    label: staffName
                };
            });
        }

        this.addGuestService.setState(this.staff, 'staff');
    }

    resetStaff () {
        let item = this.itemService.getItem();
        delete item.staff;
        this.itemService.setItem(item);

        if (this.staff) {
            for (let staff of this.staff) {
                delete (staff.disabled);
            }
        }
    }

    canDisplayStaff () {
        return this.propertyService.get('loadStaff');
    }

    canDisplayGuests () {
        return !!(this.availabilityService.hasSingleTimes()
            && this.itemService.hasItem()
            && this.itemService.getItem().preferred_time);
    }

    displayReview () {
        return !!(this.basket
            && this.basket.contents
            && this.basket.contents.guest_details
            && this.basket.contents.guest_details.find(guest => {
                return guest.id && guest.item_details.length > 0;
            }));
    }

    basketValidUntil () {
        if (this.basket.contents.expires_at) {
            return moment(`${this.basket.contents.expires_at}+00:00`).format('LLLL');
        }
    }

    displayNextActions () {
        return !!((!this.guests
            || (this.guests
                && this.guests.length === 0))
            && this.displayReview());
    }

    createOrder () {
        this.loadingService.startLoading('order');
        this.saving = true;

        this.$state.go('app.admin.order-create');
    }

    itemAddedToBasket () {
        this.resetAvailability(true, true);

        return this.suggestionsService.getSuggestions();
    }

    selectEvent (type, eventId) {
        this.showSuggestions = false;
        this.selectedEvent = this.data.events.find(event => event.type_name === type && event.id === eventId);
    }
}

BasketCreateController.$inject = ['$state', 'eventsService', 'basket', 'messageService', 'availabilityService', 'quantityService', 'propertyService', 'addGuestService', 'suggestionsService', 'itemService', 'loadingService'];

export default BasketCreateController;



// WEBPACK FOOTER //
// ./frontend/admin/app/modules/basket/controllers/basket.create.controller.js